import { useState, useEffect, useRef } from "react";
import { UI } from "../constants/tokens";
import { useTheme, cssVar } from "../utils/theme";

const SCRIPT_SRC = "https://s3.tradingview.com/tv.js";

// ── Script loader singleton ──────────────────────────────────────────────────
let _loaded = false;
let _loading = false;
const _queue = [];

function loadTVScript(cb) {
  if (_loaded) { cb(); return; }
  _queue.push(cb);
  if (_loading) return;
  _loading = true;
  const s = document.createElement("script");
  s.src = SCRIPT_SRC;
  s.async = true;
  s.onload = () => { _loaded = true; _queue.forEach(fn => fn()); _queue.length = 0; };
  document.head.appendChild(s);
}

// ── Widget DOM cache: "symbol|theme" → { el, ready } ───────────────────────
// Keyed by theme as well as symbol: the widget renders inside a cross-origin
// iframe, so its colours are fixed at construction time and cannot be
// restyled afterwards. A theme switch therefore needs a fresh widget, and the
// old one stays parked in case the user switches back.
// Instead of destroying the TradingView iframe on unmount, we move it to a
// hidden off-screen parking element and restore it on the next open.
// This eliminates the 20-second reload on every panel re-open.
const widgetCache = new Map();

function getParkingLot() {
  let lot = document.getElementById("tv-parking");
  if (!lot) {
    lot = document.createElement("div");
    lot.id = "tv-parking";
    // Keep at full widget dimensions so the iframe doesn't suspend/resize.
    // The width of the viewport, not a fixed 800px: on a phone that ran
    // past the screen's edge (23/10/2026, ?debug=overflow on the stock page
    // after leaving the chart tab: "LEAK div w=800"). The chart is about as
    // wide as the screen anyway, and a resize is sent when it comes back.
    lot.style.cssText =
      "position:fixed;left:0;top:0;width:100%;height:440px;" +
      "visibility:hidden;pointer-events:none;z-index:-1;overflow:hidden;";
    document.body.appendChild(lot);
  }
  return lot;
}

// ── Component ────────────────────────────────────────────────────────────────
// `height`: 440 in the stock panel; the full-screen view on the company page
// (PriceChart, "גרף מלא") passes the height of the screen.
export default function TradingViewChart({ symbol, height = 440 }) {
  const wrapperRef = useRef(null);
  const theme = useTheme();
  const cacheKey = `${symbol}|${theme}`;
  // Start without the placeholder if the widget is already loaded for this key.
  const [loading, setLoading] = useState(() => !widgetCache.get(cacheKey)?.ready);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const cached = widgetCache.get(cacheKey);

    // ── Case 1: widget already exists for this symbol ──────────────────────
    if (cached) {
      setLoading(!cached.ready);
      cached.el.style.height = `${height}px`;
      wrapper.appendChild(cached.el);
      // Trigger resize so TradingView adapts to the (possibly different) wrapper width.
      window.dispatchEvent(new Event("resize"));

      if (!cached.ready) {
        // Widget was parked before it finished loading — keep watching.
        const obs = new MutationObserver(() => {
          const iframe = cached.el.querySelector("iframe");
          if (!iframe) return;
          obs.disconnect();
          iframe.addEventListener("load", () => { cached.ready = true; setLoading(false); }, { once: true });
        });
        obs.observe(cached.el, { childList: true, subtree: true });
        return () => { obs.disconnect(); getParkingLot().appendChild(cached.el); };
      }

      return () => { getParkingLot().appendChild(cached.el); };
    }

    // ── Case 2: first load for this symbol ────────────────────────────────
    setLoading(true);
    const safeId = "tv_" + cacheKey.replace(/[^a-z0-9]/gi, "_").toLowerCase();

    const el = document.createElement("div");
    el.id   = safeId;
    el.style.cssText = `width:100%;height:${height}px;`;
    wrapper.appendChild(el);

    const entry = { el, ready: false };
    widgetCache.set(cacheKey, entry);

    // Detect when TradingView inserts its iframe → use that as the "ready" signal.
    const obs = new MutationObserver(() => {
      const iframe = el.querySelector("iframe");
      if (!iframe) return;
      obs.disconnect();
      iframe.addEventListener("load", () => { entry.ready = true; setLoading(false); }, { once: true });
    });
    obs.observe(el, { childList: true, subtree: true });

    loadTVScript(() => {
      if (!window.TradingView) return;
      new window.TradingView.widget({
        autosize:          true,
        height:            440,
        symbol,
        interval:          "D",
        timezone:          "Asia/Jerusalem",
        // The iframe cannot read our CSS variables, so the active theme's
        // values are resolved to concrete colours and handed over.
        theme:             theme === "dark" ? "dark" : "light",
        style:             "1",
        // English (2026-09-26): he_IL mixed a Hebrew UI with the English
        // company name and tickers inside a canvas we cannot set dir on, and
        // the legend came out scrambled. Dates and prices read the same.
        locale:            "en",
        toolbar_bg:        cssVar("--si-surface-sunken"),
        enable_publishing: false,
        hide_top_toolbar:  false,
        hide_legend:       false,
        save_image:        false,
        container_id:      safeId,
        backgroundColor:   cssVar("--si-surface-sunken"),
        gridColor:         cssVar("--si-chart-grid"),
      });
    });

    return () => { obs.disconnect(); getParkingLot().appendChild(el); };
    // symbol and theme are both already folded into cacheKey; they are listed
    // so the exhaustive-deps rule can see every value the effect reads.
  }, [cacheKey, symbol, theme, height]);

  return (
    <div style={{ position: "relative", width: "100%", height, borderRadius: 6, overflow: "hidden", background: UI.surface.sunken }}>

      {/* Loading placeholder — hidden once iframe fires its load event */}
      {loading && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 10,
          background: UI.surface.sunken, borderRadius: 6,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12,
        }}>
          <div
            className="si-spin"
            style={{ width: 28, height: 28, borderRadius: "50%", border: `3px solid ${UI.line.hairline}`, borderTopColor: UI.interactive }}
          />
          <span style={{ fontSize: 14, color: UI.text.secondary, fontWeight: 600 }}>טוען גרף...</span>
        </div>
      )}

      {/* Widget mount point — the cached DOM element is appended here */}
      <div ref={wrapperRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}
